<script setup>
import Layout from "../../layouts/main.vue";
import MethodService from "../../service/MethodService";
import mushroom from "cem-primary-api";
import { onMounted, reactive, ref, computed } from "vue";
import DataForm from "./dataCategoryService";
import { FormInstance } from "element-plus";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import toastr from "toastr";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const ruleFormRef = ref(FormInstance);
const formRef = ref(null);
const loading = ref(false);
const dialogVisible = ref(false);
const dialogType = ref("add");
const originalCategoryName = ref("");

const dialogTitle = computed(() => {
  if (dialogType.value === "add") return t("t_add_category");
  return (
    t("t_edit_category") +
    (originalCategoryName.value ? ": " + originalCategoryName.value : "")
  );
});

const tableRules = reactive(MethodService.copyObject(DataForm.tableRules));
const validForm = reactive({ value: DataForm.tableRules.dataSearch.valid });
const dataTables = reactive({ value: [] });

const formData = reactive({
  id: null,
  group_name: "",
  description: "",
  create_by: null,
});

const rules = {
  group_name: [
    { required: true, message: t("valid_category_required"), trigger: "blur" },
  ],
};

const updateUrlQuery = (extraParams = {}) => {
  const query = {
    ...tableRules.dataSearch.value,
    _limit: tableRules.limit,
    _offset: tableRules.offset,
    _sort: tableRules.sort,
    showFormSearch: tableRules.showFormSearch,
    page: tableRules.page,
    ...extraParams,
  };
  router.replace({ query }).catch(() => {});
};

const syncStateFromUrl = () => {
  const q = route.query;
  if (q.limit) tableRules.limit = parseInt(q.limit);
  if (q.page) tableRules.page = parseInt(q.page);
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;

  if (q._sort) {
    tableRules.sort = q._sort;
    tableRules.defaultSort[0] = tableRules.sort.replace("-", "");
    tableRules.defaultSort[1] = tableRules.sort.startsWith("-")
      ? "descending"
      : "ascending";
  }

  if (q.showFormSearch) {
    tableRules.showFormSearch = q.showFormSearch === "true";
  }

  Object.keys(tableRules.dataSearch.value).forEach((key) => {
    if (q[key] !== undefined) tableRules.dataSearch.value[key] = q[key];
  });
};

const getListData = async () => {
  loading.value = true;
  updateUrlQuery();

  const filter = {
    _limit: tableRules.limit,
    _offset: tableRules.offset,
    _sort: tableRules.sort,
    ...tableRules.dataSearch.value,
  };

  try {
    const [listRes, countRes] = await Promise.all([
      mushroom.service_health_category.views.getListAsync(filter),
      mushroom.service_health_category.views.getCountAsync(
        tableRules.dataSearch.value,
      ),
    ]);

    dataTables.value = listRes.result || [];
    tableRules.total = countRes.result?.[0]?.total || 0;
  } catch (e) {
    MethodService.showError(e.code);
  } finally {
    loading.value = false;
  }
};

const fn_showFormTimKiem = () => {
  tableRules.showFormSearch = !tableRules.showFormSearch;
  updateUrlQuery({ showFormSearch: tableRules.showFormSearch });
};

const fn_TimKiem = (formEl) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (!valid) return;
    Object.keys(tableRules.dataSearch.value).forEach((key) => {
      const val = tableRules.dataSearch.value[key];
      if (typeof val === "string")
        tableRules.dataSearch.value[key] = val.trim();
    });
    tableRules.page = 1;
    tableRules.offset = 0;
    getListData();
  });
};

const fn_tableSortChange = (column) => {
  MethodService.tableSortChange(column, tableRules);
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;
  getListData();
};

const fn_tableSizeChange = (limit) => {
  tableRules.limit = limit;
  tableRules.page = 1;
  tableRules.offset = 0;
  getListData();
};

const fn_tableCurentChange = (page) => {
  tableRules.page = page;
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;
  getListData();
};

const handleAdd = () => {
  dialogType.value = "add";
  originalCategoryName.value = "";
  Object.assign(formData, {
    id: null,
    group_name: "",
    description: "",
    create_by: "Manual",
  });
  dialogVisible.value = true;
};

const handleEdit = (row) => {
  dialogType.value = "edit";
  originalCategoryName.value = row.group_name;
  Object.assign(formData, {
    id: row.group_id,
    group_name: row.group_name,
    description: row.description,
    create_by: row.create_by,
  });
  dialogVisible.value = true;
};

const handleDelete = (row) => {
  if (!row.group_id) return;
  Swal.fire({
    title: t("t_warning"),
    text: t("swal_confirm_delete_category"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await mushroom.service_health_category.deleteAsync({
          id: row.group_id,
        });
        toastr.success(t("toastr_delete_category_success"));
        getListData();
      } catch (e) {
        if (
          e?.code === 400 &&
          e?.subCode === 1003 &&
          e?.meta?.service?.data?.length > 0
        ) {
          const listName = e.meta.service.data
            .map((item) => `<li>${item.service_name}</li>`)
            .join("");
          const htmlContent = DOMPurify.sanitize(
            `<div class="text-start">${t(
              "toastr_category_in_use_service_error",
            )}</div><ul class="text-start mt-2">${listName}</ul>`,
          );
          Swal.fire({
            title: t("t_warning"),
            html: htmlContent,
            icon: "warning",
            confirmButtonColor: "#34c38f",
            confirmButtonText: t("swal_accept"),
          });
        } else if (e?.code === 400) {
          if (e.subCode === 1003) toastr.error(t("toastr_category_in_use"));
          else toastr.error(t("toastr_error"));
        } else if (e?.code === 403) {
          if (e.subCode === 1101)
            toastr.error(t("t_service_group_not_deletable"));
          else toastr.error(t("toastr_error"));
        } else toastr.error(t("toastr_error"));
      }
    }
  });
};

const submitForm = async () => {
  if (!formRef.value) return;
  await formRef.value.validate(async (valid) => {
    if (!valid) return;
    try {
      if (dialogType.value === "add") {
        const { result } = await mushroom.service_health_category.createAsync(
          formData,
        );
        if (result) toastr.success(t("toastr_add_category_success"));
      } else {
        const { result } =
          await mushroom.service_health_category.partialUpdateAsync(formData);
        if (result) toastr.success(t("toastr_update_category_success"));
      }
      dialogVisible.value = false;
      getListData();
    } catch (e) {
      if (e?.code === 400) {
        if (e.subCode === 1002) toastr.error(t("toastr_category_duplicate"));
        else toastr.error(t("toastr_error"));
      } else toastr.error(t("toastr_error"));
    }
  });
};

onMounted(() => {
  syncStateFromUrl();
  getListData();
});
</script>

<template>
  <Layout>
    <div class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h3 class="text-uppercase mb-0">
          {{ $t("t_service_category_management") }}
        </h3>
        <div class="text-end">
          <div class="btn-header">
            <button
              class="btn btn-soft-secondary btn-border me-2"
              @click="fn_showFormTimKiem()"
            >
              <i class="ri-search-line"></i>
              {{ $t("t-search") }}
            </button>
            <button class="btn btn-secondary btn-border" @click="handleAdd">
              <i class="ri-add-circle-line"></i>
              {{ $t("t-add") }}
            </button>
          </div>
        </div>
      </div>

      <div class="card-body p-0 pb-3">
        <div class="main-body">
          <div class="main-body-search mx-3">
            <el-collapse-transition>
              <div v-show="tableRules.showFormSearch">
                <b-card class="b-shadow mb-0">
                  <el-form
                    ref="ruleFormRef"
                    :model="tableRules.dataSearch.value"
                    :rules="validForm.value"
                    label-position="top"
                    @submit.prevent
                  >
                    <b-row align-v="end">
                      <b-col md="6">
                        <el-form-item
                          :label="$t('t_category_name')"
                          prop="group_name"
                        >
                          <el-input
                            clearable
                            v-model="tableRules.dataSearch.value['group_name']"
                            :placeholder="$t('t_place_category_name')"
                          ></el-input>
                        </el-form-item>
                      </b-col>
                      <b-col md="3">
                        <el-form-item
                          :label="$t('t_create_by')"
                          prop="create_by"
                        >
                          <el-select
                            v-model="tableRules.dataSearch.value['create_by']"
                            :placeholder="$t('t_all')"
                            clearable
                            style="width: 100%"
                          >
                            <el-option label="System" value="System" />
                            <el-option label="Manual" value="Manual" />
                          </el-select>
                        </el-form-item>
                      </b-col>
                      <b-col md="3">
                        <el-form-item label="&nbsp;">
                          <div class="d-flex justify-content-center">
                            <a
                              class="btn btn-soft-secondary btn-border me-2"
                              style="width: 80px"
                              @click="fn_showFormTimKiem()"
                            >
                              {{ $t("t-hide") }}
                            </a>
                            <button
                              class="btn btn-secondary bg-gradient btn-border waves-effect waves-light"
                              style="width: 100px"
                              native-type="submit"
                              @click="fn_TimKiem(ruleFormRef)"
                            >
                              {{ $t("t-search") }}
                            </button>
                          </div>
                        </el-form-item>
                      </b-col>
                    </b-row>
                  </el-form>
                </b-card>
              </div>
            </el-collapse-transition>
          </div>

          <div class="d-flex justify-content-end mt-3 mb-3 px-3">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="tableRules.limit"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
            />
          </div>

          <el-table
            border
            size="small"
            max-height="520"
            stripe
            :data="dataTables.value"
            style="width: 100%"
            v-loading="loading"
            :empty-text="$t('t-no-data')"
            @sort-change="fn_tableSortChange"
            :default-sort="{
              prop: tableRules.defaultSort[0],
              order: tableRules.defaultSort[1],
            }"
          >
            <el-table-column :label="$t('t-stt')" width="60" align="center">
              <template #default="scope">
                {{
                  scope.$index + (tableRules.page - 1) * tableRules.limit + 1
                }}
              </template>
            </el-table-column>
            <el-table-column
              prop="group_name"
              :label="$t('t_category_name')"
              align="center"
              min-width="150"
              sortable="custom"
            >
              <template #default="scope">
                <div class="text-start">{{ scope.row.group_name }}</div>
              </template>
            </el-table-column>

            <el-table-column
              prop="description"
              :label="$t('t_description')"
              align="center"
              min-width="250"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="text-start">{{ scope.row.description }}</div>
              </template>
            </el-table-column>
            <el-table-column
              prop="create_by"
              :label="$t('t_create_by')"
              align="center"
              width="120"
              sortable="custom"
            >
              <template #default="scope">
                <span
                  v-if="scope.row.create_by === 'System'"
                  class="badge badge-soft-danger"
                >
                  System
                </span>
                <span
                  v-else-if="scope.row.create_by === 'Manual'"
                  class="badge badge-soft-secondary"
                >
                  Manual
                </span>
                <span v-else class="badge badge-soft-info">{{
                  scope.row.create_by
                }}</span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('t-action')"
              width="120"
              align="center"
              fixed="right"
            >
              <template #default="scope">
                <div class="btn-group-thao-tac">
                  <el-tooltip
                    :content="$t('action_option')"
                    effect="light"
                    placement="top"
                  >
                    <button
                      class="btn btn-soft-success waves-effect waves-light btn-sm me-1"
                      :disabled="scope.row.create_by === 'System'"
                      @click="handleEdit(scope.row)"
                    >
                      <i class="ri-edit-box-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    :content="$t('action_delete')"
                    effect="light"
                    placement="top"
                  >
                    <button
                      class="btn btn-soft-danger waves-effect waves-light btn-sm"
                      :disabled="scope.row.create_by === 'System'"
                      @click="handleDelete(scope.row)"
                    >
                      <i class="ri-delete-bin-6-line"></i>
                    </button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <div class="d-flex justify-content-end mt-3 px-3">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="tableRules.limit"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Cập nhật/Thêm mới -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="500px"
      append-to-body
      destroy-on-close
      align-center
    >
      <el-form
        ref="formRef"
        :model="formData"
        :rules="rules"
        label-position="top"
      >
        <el-form-item :label="$t('t_category_name')" prop="group_name">
          <el-input
            v-model="formData.group_name"
            :placeholder="$t('t_place_category_name')"
          />
        </el-form-item>
        <el-form-item :label="$t('t_description')" prop="description">
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="2"
            :placeholder="$t('t_place_description')"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="text-center">
          <button class="btn btn-secondary btn-border" @click="submitForm">
            {{ dialogType === "add" ? $t("t-add") : $t("t-update") }}
          </button>
        </div>
      </template>
    </el-dialog>
  </Layout>
</template>
